import { reportPlatformError } from "$root/api/error-reporting";
import { platformToast } from "$root/notification-system/toast";
import { readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { isBotCompute } from "../../common";
import type { PortfolioLabContextProps, PortfolioLabInvestmentItem } from "../../ctx";
import { portfolioLabRepository } from "../../repository";
import { baselineCoveringComposition } from "./baseline";

/**
 * Runs one server write that can move a column's composition revision (composition PUT, reset,
 * scenario PUT) on that column's write lane. It is the only code that touches `pendingWrites` and
 * `compositionRevision`.
 *
 * `pendingWrites` goes up synchronously, before the write is queued, so the charts read as stale
 * from the moment the edit is on screen. When the write settles it comes back down, whatever the
 * outcome, including a 404 for a column deleted elsewhere, so a column is never left pending. On success the
 * revision the server answered with is stored. The lane keeps this column's writes in submission
 * order (T6), so the acknowledgements, and the revisions they carry, arrive in that order too.
 *
 * A saved write that moves the revision away from the one a queued compute will stamp drops the
 * column's `pendingCompute`: the backend discards that compute as superseded. A compute SphereAI
 * started read the revision the column was at, so any write that moves it drops that one.
 *
 * `write` receives the live item, resolved when its lane turn comes: a column removed in the
 * meantime is skipped rather than written to. A write made of several server calls passes each
 * intermediate revision to `recordRevision`, so a later call that fails does not lose the revision
 * an earlier one already moved. The returned promise rejects with the write's error; surfacing it
 * is the caller's job.
 */
export async function runCompositionWrite(
	ctx: PortfolioLabContextProps,
	virtualUuid: string,
	write: (
		investment: PortfolioLabInvestmentItem,
		recordRevision: (revision: number) => void,
	) => Promise<{ compositionRevision: number }>,
): Promise<void> {
	patchInvestment(ctx, virtualUuid, (i) => ({ pendingWrites: i.pendingWrites + 1 }));
	let revision: number | undefined;
	const recordRevision = (r: number) => {
		revision = r;
	};
	try {
		await ctx.investmentWriteLanes.run(virtualUuid, async () => {
			const investment = readAtom(ctx.investments$).find((inv) => inv.virtualUuid === virtualUuid);
			if (investment) {
				recordRevision((await write(investment, recordRevision)).compositionRevision);
			}
		});
	} finally {
		patchInvestment(ctx, virtualUuid, (i) => ({
			pendingWrites: Math.max(0, i.pendingWrites - 1),
			...(revision != null && { compositionRevision: revision }),
			...(revision != null && supersedesPendingCompute(i, revision) && { pendingCompute: null }),
		}));
	}
}

function supersedesPendingCompute(item: PortfolioLabInvestmentItem, revision: number): boolean {
	const pending = item.pendingCompute;
	if (pending == null) {
		return false;
	}
	return revision !== (isBotCompute(pending) ? item.compositionRevision : pending.revision);
}

/**
 * Sync one virtual investment's composition to the server, through {@link runCompositionWrite}.
 *
 * Every composition mutation must persist through this helper — never `await` the repository
 * directly — so writes reach the server in the order the user made them (T6) and the column's
 * revision bookkeeping stays with its one owner.
 *
 * A column with `baselineUnsaved` set is rebaselined first, in the same lane turn, so no other write
 * lands between the two PUTs. A failed rebaseline leaves the flag set and skips the save; the next
 * sync tries both again.
 *
 * Failure surface: the optimistic atom state is kept (no rollback/refetch). The user is toasted
 * and the investment's `compositionSaveStatus` becomes `"error"` so the column header flags the
 * divergence and the charts read as stale; the next successful sync clears it back to `"idle"`. The
 * returned promise never rejects: it resolves `true` when the server holds the composition, so a
 * caller that computes next can skip the compute otherwise.
 *
 * The status flip needs no caller callback: everything that shows it — the column header's
 * sync-error icon included — subscribes to `investments$` and repaints from this write. An
 * `api.refreshHeader()` would destroy and rebuild every header row, detaching the node an open
 * column menu is anchored to (see LabPortfolioGroupHeader).
 */
export function syncComposition(ctx: PortfolioLabContextProps, virtualUuid: string): Promise<boolean> {
	return persistComposition(ctx, virtualUuid, async (investment, recordRevision) => {
		const scenarioId = readAtom(ctx.selectedScenarioId$);
		if (investment.baselineUnsaved) {
			recordRevision((await rebaselineToLocalBaseline(ctx, investment, scenarioId)).compositionRevision);
		}
		return portfolioLabRepository.updateVirtualInvestment(investment, scenarioId);
	});
}

/**
 * Moves the server's baseline to the local `initialComposition` / `initialAum`, so the Δ and Reset
 * match them after a reload. Built from the baseline, never from `currentComposition`: an edit made
 * after the baseline moved must not leak into it. Clears `baselineUnsaved` unless the baseline moved
 * again while the write was out, in which case the next write rebaselines once more.
 */
async function rebaselineToLocalBaseline(
	ctx: PortfolioLabContextProps,
	investment: PortfolioLabInvestmentItem,
	scenarioId: string | null,
): Promise<{ compositionRevision: number }> {
	const response = await portfolioLabRepository.rebaselineVirtualInvestment(
		{
			...investment,
			currentComposition: baselineCoveringComposition(investment.initialComposition, investment.currentComposition),
			aum: investment.initialAum,
		},
		scenarioId,
	);
	patchInvestment(ctx, investment.virtualUuid, (i) =>
		i.initialComposition === investment.initialComposition && i.initialAum === investment.initialAum
			? { baselineUnsaved: false }
			: {},
	);
	return response;
}

async function persistComposition(
	ctx: PortfolioLabContextProps,
	virtualUuid: string,
	write: Parameters<typeof runCompositionWrite>[2],
): Promise<boolean> {
	try {
		await runCompositionWrite(ctx, virtualUuid, write);
	} catch (error) {
		reportPlatformError(error, "ERROR", "portfolio", {
			message: `Unable to sync the lab composition (${virtualUuid})`,
		});
		patchInvestment(ctx, virtualUuid, () => ({ compositionSaveStatus: "error" }));
		platformToast({
			icon: "Icon-full-error",
			severity: "error",
			children:
				"Failed to save the latest composition changes. Your edits are kept — retry an edit or reset the column.",
		});
		return false;
	}
	// Only clear a previous sync error — "saving" belongs to the Update-composition commit flow.
	patchInvestment(ctx, virtualUuid, (i) =>
		i.compositionSaveStatus === "error" ? { compositionSaveStatus: "idle" } : {},
	);
	return true;
}

/**
 * Reverts the column's server-side composition to its baseline, through {@link runCompositionWrite}.
 * A server baseline that lags the local one is replaced instead: rebaselining to the local baseline
 * leaves the server's composition at it, which is the reset the user sees. Rejects with the write's
 * error.
 */
export function resetComposition(ctx: PortfolioLabContextProps, virtualUuid: string): Promise<void> {
	return runCompositionWrite(ctx, virtualUuid, (investment) =>
		investment.baselineUnsaved
			? rebaselineToLocalBaseline(ctx, investment, readAtom(ctx.selectedScenarioId$))
			: portfolioLabRepository.resetVirtualInvestment(virtualUuid),
	);
}

function patchInvestment(
	ctx: PortfolioLabContextProps,
	virtualUuid: string,
	patch: (investment: PortfolioLabInvestmentItem) => Partial<PortfolioLabInvestmentItem>,
): void {
	writeAtom(ctx.investments$, (items) => items.map((i) => (i.virtualUuid === virtualUuid ? { ...i, ...patch(i) } : i)));
}
